import {
  __name
} from "./chunk-7QVYU63E.js";

// src/result.ts
function ok(value) {
  return {
    ok: true,
    value
  };
}
__name(ok, "ok");
function err(error) {
  return {
    ok: false,
    error
  };
}
__name(err, "err");
function isOk(result) {
  return result.ok;
}
__name(isOk, "isOk");
function isErr(result) {
  return !result.ok;
}
__name(isErr, "isErr");

// src/money.ts
import { Decimal } from "decimal.js";
function addMoney(a, b) {
  return new Decimal(a).plus(new Decimal(b));
}
__name(addMoney, "addMoney");
function subtractMoney(a, b) {
  return new Decimal(a).minus(new Decimal(b));
}
__name(subtractMoney, "subtractMoney");
function multiplyMoney(paise, factor) {
  return new Decimal(paise).mul(new Decimal(factor)).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
}
__name(multiplyMoney, "multiplyMoney");
function divideMoney(paise, divisor) {
  return new Decimal(paise).div(new Decimal(divisor)).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
}
__name(divideMoney, "divideMoney");
function formatMoney(paise, options) {
  const decimals = options?.decimals ?? 2;
  const showSymbol = options?.showSymbol ?? true;
  const currency = options?.currency ?? "INR";
  const locale = options?.locale ?? "en-IN";
  const signDisplay = options?.signDisplay ?? "auto";
  const major = new Decimal(paise).div(100).toNumber();
  if (!showSymbol) {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      signDisplay
    }).format(major);
  }
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay
  }).format(major);
}
__name(formatMoney, "formatMoney");
function rupeesToPaise(rupees) {
  return new Decimal(rupees).mul(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
}
__name(rupeesToPaise, "rupeesToPaise");

// src/index.ts
import { Decimal as Decimal3 } from "decimal.js";

// src/date.ts
import { toZonedTime, fromZonedTime, format } from "date-fns-tz";
var DEFAULT_TZ = "Asia/Kolkata";
function toLocalTime(utcDate, tz = DEFAULT_TZ) {
  return toZonedTime(utcDate, tz);
}
__name(toLocalTime, "toLocalTime");
function fromLocalTime(localDate, tz = DEFAULT_TZ) {
  return fromZonedTime(localDate, tz);
}
__name(fromLocalTime, "fromLocalTime");
function formatLocalTime(utcDate, fmt = "yyyy-MM-dd'T'HH:mm:ssXXX", tz = DEFAULT_TZ) {
  return format(toZonedTime(utcDate, tz), fmt, {
    timeZone: tz
  });
}
__name(formatLocalTime, "formatLocalTime");
function nowUtc() {
  return /* @__PURE__ */ new Date();
}
__name(nowUtc, "nowUtc");
function formatNullableDate(dateVal, fallback = "\u2014", fmt = "yyyy-MM-dd", tz = DEFAULT_TZ) {
  if (!dateVal) return fallback;
  const d = typeof dateVal === "string" ? new Date(dateVal) : dateVal;
  if (isNaN(d.getTime())) return fallback;
  return format(toZonedTime(d, tz), fmt, {
    timeZone: tz
  });
}
__name(formatNullableDate, "formatNullableDate");

// src/state-machine.ts
function createStateMachine(states, transitions, options) {
  return {
    async transition(from, to, context) {
      if (!states.includes(from) || !states.includes(to)) {
        return err({
          code: "INVALID_TRANSITION",
          message: `Transition from '${from}' to '${to}' involves invalid states`,
          details: {
            current: from,
            next: to
          }
        });
      }
      const allowed = transitions[from]?.includes(to) ?? false;
      if (!allowed) {
        return err({
          code: "INVALID_TRANSITION",
          message: `Transition from '${from}' to '${to}' is not allowed`,
          details: {
            current: from,
            next: to
          }
        });
      }
      if (options?.onTransition) {
        try {
          await options.onTransition(from, to, context);
        } catch (error) {
          return err({
            code: "TRANSITION_HOOK_FAILED",
            message: `Transition hook failed: ${error?.message || String(error)}`,
            details: {
              current: from,
              next: to
            }
          });
        }
      }
      return ok(to);
    }
  };
}
__name(createStateMachine, "createStateMachine");

// src/template-registry.ts
var registry = /* @__PURE__ */ new Map();
function registerTemplate(key, renderer) {
  registry.set(key, renderer);
}
__name(registerTemplate, "registerTemplate");
function renderTemplate(key, payload) {
  const renderer = registry.get(key);
  if (!renderer) {
    return err({
      code: "TEMPLATE_NOT_FOUND",
      message: `Notification template '${key}' not registered`
    });
  }
  return ok(renderer(payload));
}
__name(renderTemplate, "renderTemplate");

// src/pii.ts
function maskMobile(mobile) {
  if (!mobile) return null;
  const cleaned = mobile.replace(/\D/g, "");
  if (cleaned.length < 4) return cleaned;
  return "XXXXXX" + cleaned.slice(-4);
}
__name(maskMobile, "maskMobile");
function maskEmail(email) {
  if (!email) return null;
  const parts = email.split("@");
  if (parts.length !== 2) return email;
  const [local, domain] = parts;
  if (local.length <= 1) return local + "***@" + domain;
  return local[0] + "***@" + domain;
}
__name(maskEmail, "maskEmail");
function maskAadhaar(aadhaar) {
  if (!aadhaar) return null;
  return "XXXX-XXXX-" + aadhaar.replace(/\D/g, "").slice(-4);
}
__name(maskAadhaar, "maskAadhaar");
function maskPan(pan) {
  if (!pan) return null;
  return pan.slice(0, 5) + "****" + pan.slice(-1);
}
__name(maskPan, "maskPan");
function maskBankAccount(account) {
  if (!account) return null;
  return "XXXXXXX" + account.replace(/\D/g, "").slice(-4);
}
__name(maskBankAccount, "maskBankAccount");
function maskObjectPii(obj) {
  if (!obj || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(maskObjectPii);
  const masked = {
    ...obj
  };
  for (const key in masked) {
    const val = masked[key];
    if (typeof val === "string") {
      const lk = key.toLowerCase().replace(/_/g, "");
      if (lk === "aadhaarnumber" || lk === "aadhaar" || lk === "aadhaarhash") {
        masked[key] = maskAadhaar(val);
      } else if (lk === "pannumber" || lk === "pan") {
        masked[key] = maskPan(val);
      } else if (lk === "bankaccountnumber" || lk === "bankaccount" || lk === "accountnumber") {
        masked[key] = maskBankAccount(val);
      } else if (lk === "mobile" || lk === "mobilenumber" || lk === "phone" || lk === "phonenumber") {
        masked[key] = maskMobile(val);
      } else if (lk === "email" || lk === "emailaddress") {
        masked[key] = maskEmail(val);
      } else if (lk === "photourl" || lk === "documenturl") {
        masked[key] = "[DOCUMENT_REDACTED]";
      }
    } else if (typeof val === "object") {
      masked[key] = maskObjectPii(val);
    }
  }
  return masked;
}
__name(maskObjectPii, "maskObjectPii");
function maskPiiText(text) {
  let masked = text.replace(/\b\d{10}\b/g, (m) => "XXXXXX" + m.slice(-4));
  masked = masked.replace(/\b([a-zA-Z0-9._%+-]+)@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/g, (_m, local, domain) => {
    if (local.length <= 1) return local + "***@" + domain;
    return local[0] + "***@" + domain;
  });
  return masked;
}
__name(maskPiiText, "maskPiiText");

// src/cn.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
__name(cn, "cn");

// src/outbox.ts
function emitAfterCommit(cls, eventEmitter, eventName, payload) {
  const queue = cls?.get("eventQueue");
  if (queue) {
    queue.push({
      name: eventName,
      payload
    });
  } else if (eventEmitter) {
    eventEmitter.emit(eventName, payload);
  }
}
__name(emitAfterCommit, "emitAfterCommit");

// src/denominations.ts
import { Decimal as Decimal2 } from "decimal.js";
function calculateDenominationPaise(denominations) {
  let totalPaise = new Decimal2(0);
  for (const [denomStr, count] of Object.entries(denominations)) {
    const denomRupees = new Decimal2(denomStr);
    const countDec = new Decimal2(count);
    if (!denomRupees.isFinite() || !countDec.isFinite() || countDec.isNegative()) {
      continue;
    }
    totalPaise = totalPaise.plus(denomRupees.mul(100).mul(countDec));
  }
  return totalPaise;
}
__name(calculateDenominationPaise, "calculateDenominationPaise");
function validateDenominationSum(denominations, expectedPaise) {
  const calculatedPaise = calculateDenominationPaise(denominations);
  const expectedDec = new Decimal2(expectedPaise);
  if (!calculatedPaise.equals(expectedDec)) {
    return err({
      code: "BAD_REQUEST",
      message: `Denominations sum (${calculatedPaise.toString()} paise) does not match expected amount (${expectedPaise} paise)`
    });
  }
  return ok(void 0);
}
__name(validateDenominationSum, "validateDenominationSum");
export {
  Decimal3 as Decimal,
  addMoney,
  calculateDenominationPaise,
  cn,
  createStateMachine,
  divideMoney,
  emitAfterCommit,
  err,
  formatLocalTime,
  formatMoney,
  formatNullableDate,
  fromLocalTime,
  isErr,
  isOk,
  maskAadhaar,
  maskBankAccount,
  maskEmail,
  maskMobile,
  maskObjectPii,
  maskPan,
  maskPiiText,
  multiplyMoney,
  nowUtc,
  ok,
  registerTemplate,
  renderTemplate,
  rupeesToPaise,
  subtractMoney,
  toLocalTime,
  validateDenominationSum
};
//# sourceMappingURL=index.js.map